import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import { 
  Gem, 
  ShoppingBag, 
  Award, 
  Globe, 
  Users, 
  Coffee,
  ShieldCheck,
  Sparkles,
  MapPin,
  Clock,
  Phone,
  Mail,
  ArrowRight,
  CheckCircle,
  Star,
  DollarSign,
  Heart,
  Truck,
  Mountain,
  Palette,
  Navigation,
  Building,
  AlertCircle
} from "lucide-react";
import SEOHead from "../components/SEOHead";

const OpalStorePage = () => {
  const [openingHours, setOpeningHours] = useState({ closed_on_tuesdays: false });

  useEffect(() => {
    fetchOpeningHours();
  }, []);

  const fetchOpeningHours = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/opening-hours/public`);
      if (response.ok) {
        const data = await response.json();
        setOpeningHours(data);
      }
    } catch (error) {
      console.error('Error fetching opening hours:', error);
    }
  };

  const directBenefits = [
    {
      icon: DollarSign,
      title: "Best Prices Guaranteed",
      description: "Buy direct from the source"
    },
    {
      icon: ShieldCheck,
      title: "Authentic Guarantee",
      description: "All opals mined on-site with certificate of authenticity"
    },
    {
      icon: Heart,
      title: "Family Owned",
      description: "Supporting local mining families since 1916"
    },
    {
      icon: Award,
      title: "Expert Guidance",
      description: "From knowledgeable staff"
    }
  ];

  const inStoreAdvantages = [
    {
      icon: Gem,
      title: "See Before You Buy",
      description: "Examine opals in person under different lighting conditions",
      color: "purple"
    },
    {
      icon: Users,
      title: "Personal Consultation",
      description: "Get expert advice on selecting the perfect opal for you",
      color: "blue"
    },
    {
      icon: Sparkles,
      title: "Custom Jewelry",
      description: "Have jewelry designed and crafted on our premises",
      color: "pink"
    },
    {
      icon: Globe,
      title: "Tax Free Shopping",
      description: "International visitors enjoy tax-free prices",
      color: "green"
    }
  ];

  const opalTypes = [
    {
      emoji: "💎",
      title: "Coober Pedy White Opal", 
      description: "Classic Australian opal with brilliant color play",
      priceRange: "Affordable to Premium"
    },
    {
      emoji: "⚫",
      title: "Black Opal",
      description: "Rare dark body tone creating spectacular contrast",
      priceRange: "Premium to Exceptional"
    },
    {
      emoji: "🪨",
      title: "Boulder Opal",
      description: "Natural ironstone backing with vibrant patterns",
      priceRange: "Mid-range to Premium"
    },
    {
      emoji: "✨",
      title: "Crystal Opal",
      description: "Transparent body with stunning color flashes",
      priceRange: "Mid-range to Premium"
    },
    {
      emoji: "🐚",
      title: "Opal Curios",
      description: "Opalised fossils, shells, and unique specimens",
      priceRange: "Collector Items"
    }
  ];

  return (
    <div>
      <SEOHead
        title="Opal Shop - Buy Direct from the Mine | Umoona Opal Mine"
        description="Buy authentic Australian opals direct from Umoona Opal Mine in Coober Pedy. Best prices guaranteed, no middle man. Visit our in-store collection."
        keywords="buy opals Coober Pedy, direct from mine, authentic Australian opals, opal shop, family owned opal mine"
        canonical="/opal-store"
        ogImage="/og-umoona.jpg"
        url="https://umoonaopalmine.com.au/opal-store"
      />
      
      {/* Hero Section with Video Background - Mobile Responsive */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden pt-20 md:pt-0">
        <div className="absolute inset-0">
          {/* Video Background */}
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
            poster="/assets/Turbo360-1043168.jpg"
          >
            <source src="/opal-store-hero.mp4" type="video/mp4" />
            {/* Fallback image if video fails to load */}
            <img 
              src="/assets/Turbo360-1043168.jpg"
              alt="Umoona Opal Shop Interior"
              className="w-full h-full object-cover"
            />
          </video>
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10 py-12">
          <div className="max-w-4xl">
            <Badge variant="secondary" className="mb-6 bg-yellow-500/90 text-gray-900 border border-yellow-300 px-6 py-3 text-base md:text-lg shadow-lg font-bold">
              ⛏️ Buy Direct from the Mine - Best Prices Guaranteed
            </Badge>
            
            <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Authentic Australian Opals
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400 mt-2">
                Straight from the Source
              </span>
            </h1>
            
            <p className="text-lg md:text-xl lg:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl">
              Just pure, authentic Coober Pedy opals from local miners. Experience the difference of buying direct from miners who know and love these precious stones.
            </p>

            {/* Quick Benefits */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20">
                <CheckCircle className="w-6 h-6 text-green-400 mb-2" />
                <div className="text-white font-semibold">Mined Locally</div>
                <div className="text-gray-200 text-sm">From the Coober Pedy Opal fields</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20">
                <CheckCircle className="w-6 h-6 text-green-400 mb-2" />
                <div className="text-white font-semibold">Direct from Source</div>
                <div className="text-gray-200 text-sm">No middleman pricing</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20">
                <CheckCircle className="w-6 h-6 text-green-400 mb-2" />
                <div className="text-white font-semibold">Expert Guidance</div>
                <div className="text-gray-200 text-sm">From knowledgeable staff</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20">
                <CheckCircle className="w-6 h-6 text-green-400 mb-2" />
                <div className="text-white font-semibold">Authentic Guarantee</div>
                <div className="text-gray-200 text-sm">Certificate included</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                asChild
                size="lg"
                className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-gray-900 font-bold px-8 py-6 text-lg shadow-xl"
              >
                <Link to="/online-store">
                  <ShoppingBag className="mr-2 w-6 h-6" />
                  Shop Online Now
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-purple-900 font-bold px-8 py-6 text-lg shadow-xl"
              >
                <a href="https://maps.app.goo.gl/USuQk4rBYc7uxHRb8" target="_blank" rel="noopener noreferrer">
                  <MapPin className="mr-2 w-6 h-6" />
                  Visit Our Store
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Buy Direct from Umoona */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-purple-100 text-purple-800 px-4 py-2 mb-4">
              Direct from Miners
            </Badge>
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
              Why Buy Direct from Umoona?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              When you buy from us, you're buying directly from the miners who discovered these opals – just authentic Australian opals at the best possible prices.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {directBenefits.map((benefit, index) => (
              <Card key={index} className="text-center hover:shadow-xl transition-all duration-300 border-2 border-purple-100">
                <CardContent className="p-8">
                  <div className="bg-gradient-to-br from-purple-500 to-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* In-Store Shopping Experience */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <Badge className="bg-blue-100 text-blue-800 px-4 py-2 mb-4">
                <Building className="w-4 h-4 inline mr-2" />
                Visit Us In Person
              </Badge>
              <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
                The In-Store Experience
              </h2>
              <p className="text-xl text-gray-600 mb-6">
                There's nothing quite like seeing Australian opals in person. Visit our Coober Pedy shop 
                to experience the full beauty of these natural treasures under expert guidance.
              </p>
              <p className="text-lg text-gray-600 mb-8">
                Our knowledgeable staff are Coober Pedy opal experts who can share stories, answer questions, and help you find the perfect opal – whether it's a gift, investment, or addition to your collection.
              </p>
              <Button asChild size="lg" className="mining-cta">
                <a href="https://maps.app.goo.gl/USuQk4rBYc7uxHRb8" target="_blank" rel="noopener noreferrer">
                  <Navigation className="mr-2 w-5 h-5" />
                  Get Directions
                </a>
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {inStoreAdvantages.map((advantage, index) => (
                <Card key={index} className={`hover:shadow-xl transition-all duration-300 border-2 border-${advantage.color}-100`}>
                  <CardContent className="p-6">
                    <div className={`bg-${advantage.color}-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4`}>
                      <advantage.icon className={`w-6 h-6 text-${advantage.color}-600`} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{advantage.title}</h3>
                    <p className="text-sm text-gray-600">{advantage.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Store Photos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img 
                src="/assets/2023-1000418.jpg"
                alt="Opal Shop Display Case"
                className="w-full h-80 object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg">
              <img 
                src="/assets/2023-1000411.jpg"
                alt="Opal Jewelry Display"
                className="w-full h-80 object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Opal Types Available */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-4">
              Our Opal Collection
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From classic Coober Pedy white opals to rare black opals, we offer the largest selection 
              of authentic Australian opals in the region.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {opalTypes.map((type, index) => (
              <Card key={index} className="hover:shadow-2xl transition-all duration-300 border-2 border-purple-100">
                <CardContent className="p-8 text-center">
                  <div className="text-6xl mb-4">{type.emoji}</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{type.title}</h3>
                  <p className="text-gray-600 mb-4">{type.description}</p>
                  <Badge className="bg-purple-100 text-purple-800">
                    {type.priceRange}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Guarantee */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              The Umoona Guarantee
            </h2>
            <p className="text-xl opacity-90 max-w-3xl mx-auto">
              Every opal from Umoona Opal Mine comes with our commitment to authenticity, quality, and service.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <ShieldCheck className="w-12 h-12 mb-4 text-yellow-400" />
              <h3 className="text-xl font-bold mb-2">Authenticity Certificate</h3>
              <p className="text-white/80">Every purchase includes documentation</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <Mountain className="w-12 h-12 mb-4 text-yellow-400" />
              <h3 className="text-xl font-bold mb-2">Mined On-Site</h3>
              <p className="text-white/80">From our own Coober Pedy opal fields</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <Users className="w-12 h-12 mb-4 text-yellow-400" />
              <h3 className="text-xl font-bold mb-2">Expert Support</h3>
              <p className="text-white/80">Lifetime advice from our mining team</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <Award className="w-12 h-12 mb-4 text-yellow-400" />
              <h3 className="text-xl font-bold mb-2">Best Value</h3>
              <p className="text-white/80">Direct pricing from the source</p>
            </div>
          </div>
        </div>
      </section>

      {/* Custom Jewelry */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-pink-100 text-pink-800 px-4 py-2 mb-4">
                <Sparkles className="w-4 h-4 inline mr-2" />
                Bespoke Jewelry Service
              </Badge>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Custom Opal Jewelry
              </h2>
              <p className="text-xl text-gray-600 mb-6">
                Have a special opal in mind? Our expert jewelers can create custom pieces designed 
                exactly to your specifications. From engagement rings to statement pendants, we bring 
                your vision to life.
              </p>
              <ul className="space-y-3 text-gray-600 mb-8">
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
                  Personal design consultation
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
                  Handcrafted on our premises
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
                  Choose your opal and metal
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
                  Traditional and modern designs
                </li>
              </ul>
              <Button asChild size="lg" className="bg-pink-600 hover:bg-pink-700">
                <Link to="/contact">
                  Inquire About Custom Jewelry
                </Link>
              </Button>
            </div>

            <Card className="p-8 bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200">
              <CardContent className="space-y-6 p-0">
                <h3 className="text-2xl font-bold text-purple-900">Get in Touch</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <Phone className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Phone</p>
                      <a href="tel:0886725288" className="text-purple-600 hover:text-purple-800 text-lg">
                        (08) 8672 5288
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <Mail className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Email</p>
                      <a href="mailto:admin@umoonaopalmine.com.au" className="text-purple-600 hover:text-purple-800">
                        admin@umoonaopalmine.com.au
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <MapPin className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Location</p>
                      <p className="text-gray-600">
                        14 Hutchison Street<br />
                        Coober Pedy SA 5723
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <Clock className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900">Hours</p>
                      <p className="text-gray-600">
                        Open Daily<br />
                        8:30 AM - 5:30 PM
                      </p>
                      {openingHours.closed_on_tuesdays && (
                        <p className="text-red-600 font-semibold mt-2 flex items-center">
                          <AlertCircle className="w-4 h-4 mr-1" />
                          Closed on Tuesdays
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <Alert className="border-purple-200 bg-purple-50 mt-6">
                  <Truck className="h-4 w-4 text-purple-600" />
                  <AlertDescription className="text-purple-800">
                    <strong>Online Orders:</strong> Australian shipping only
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-br from-purple-900 to-indigo-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Experience Authentic Australian Opals Today
          </h2>
          <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
            Whether you visit us in Coober Pedy or shop online, you're getting authentic opals 
            direct from the miners at the best possible prices.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold px-8">
              <Link to="/online-store">
                <ShoppingBag className="mr-2 w-5 h-5" />
                Shop Online Store
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white hover:text-purple-900 font-bold px-8">
              <a href="https://maps.app.goo.gl/USuQk4rBYc7uxHRb8" target="_blank" rel="noopener noreferrer">
                <MapPin className="mr-2 w-5 h-5" />
                Visit Us in Coober Pedy
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OpalStorePage;
